import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { generateDownloadAnalyticsData } from 'helpers/generateDownloadAnalyticsData';
import { getFileIdFromUrl } from 'helpers/getValueFromUrl';
/** Hooks */
import { useLocaleNavigate } from 'hooks/useLocaleNavigate';
import { DocumentsStorage } from 'storages/document-storage';

/** Actions */
import { sendAnalyticEvent } from 'data/actions/analytics';
import {
  downloadDocument,
  downloadDocumentAndUpdatedDocumentId,
  getDocuments,
  sendFileViaEmail
} from 'data/actions/documents';
import { toggleModal } from 'data/actions/modals';
import { getTrustPilotLink, getUser, setPageView } from 'data/actions/user';
import {
  convertDocumentDataSelector,
  documentByIdSelector,
  editDocumentDataSelector,
  willDownloadBySSESelector
} from 'data/selectors/documents';
/** Selectors */
import { sseFileToDownloadSelector } from 'data/selectors/events';

import { openPrintModalFromURL } from 'utils/openPrintModalFromUrl';
import { getPrevPage } from 'utils/prevPath';
import showToast from 'utils/toast';

import { LOCAL_STORAGE_KEYS } from 'ts/constants/local-storage-keys';
import { PAGE_LINKS } from 'ts/constants/page-links';
import { EModalTypes } from 'ts/enums/modal.types';
import { EServiceType } from 'ts/interfaces/services/service';
import type { ApiFile } from 'ts/types/file';
import { InternalFileType } from 'ts/types/file';

import { ThankYouBlock } from './components/thank-you-block';

export const useFileDownload = () => {
  const dispatch = useDispatch();

  const navigate = useLocaleNavigate();
  const documentById: any = useSelector(documentByIdSelector());
  const editDocument = useSelector(editDocumentDataSelector());
  const dataToConvert = useSelector(convertDocumentDataSelector());

  const message = useSelector(sseFileToDownloadSelector);
  const [isDownloading, setIsDownloading] = useState<boolean>(true);
  const [queryDocumentID, setQueryDocumentID] = useState<string | null>(null);
  const willDownloadBySSE = useSelector(willDownloadBySSESelector());
  const sseDownloadId =
    message?.data?.id || JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_TO_EVENT) || '{}')?.data?.id;

  const isIosDevice = false;

  const screenConfigName = 'transactional_no_details_no_dashboard';

  const handleClickDashboard = () => {
    dispatch(
      sendAnalyticEvent({
        event: 'dashboard_tap'
      })
    );

    navigate(PAGE_LINKS.DASHBOARD);
  };

  useEffect(() => {
    if (!documentById && !window.location.pathname.includes('account-settings')) {
      navigate(PAGE_LINKS.MAIN, { replace: true });
    }
  }, [documentById, navigate]);

  useEffect(() => {
    dispatch(getUser({}));
    dispatch(setPageView('payment-success'));
    dispatch(getTrustPilotLink());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      sendAnalyticEvent({
        event: 'purchase_success_view',
        data: {
          screen_config_name: screenConfigName,
          fromPage: getPrevPage() || ''
        }
      })
    );

    if (window.location.search) {
      const documentId = getFileIdFromUrl();
      if (documentId) setQueryDocumentID(documentId as string);
    }
  }, [dispatch, screenConfigName]);

  useEffect(() => {
    if (!message) return;
    DocumentsStorage.setDataToEvent(message);
    setIsDownloading(false);
  }, [message]);

  const handleDownloadError = (isManual: boolean = false) => {
    console.log('handleDownloadError', isManual);
    dispatch(
      sendAnalyticEvent({
        event: 'file_download_status',
        data: generateDownloadAnalyticsData({
          success: false,
          isManual
        })
      })
    );
  };

  const handleDownloadSuccess = (res: any, isManual?: boolean, inputFile?: { size: string }) => {
    if (!res) {
      return;
    }

    if (res?.processing_status === 'FAILED') {
      dispatch(
        sendAnalyticEvent({
          event: 'file_download_status',
          data: generateDownloadAnalyticsData({
            success: false,
            isManual: typeof isManual === 'boolean' ? isManual : true,
            place: PAGE_LINKS.PAYMENT_SUCCESS,
            fileId: documentById?.id
          })
        })
      );
      return dispatch(toggleModal({ type: EModalTypes.FILE_UPLOAD_ERROR, visible: true }));
    }

    dispatch(
      sendAnalyticEvent({
        event: 'file_download_status',
        data: generateDownloadAnalyticsData({
          success: true,
          isManual: typeof isManual === 'boolean' ? isManual : true,
          place: PAGE_LINKS.PAYMENT_SUCCESS,
          fileData: res,
          fileId: res?.id,
          ...(inputFile ? { inputFile: { size: Number(inputFile.size) } } : {})
        })
      })
    );
    localStorage.setItem(LOCAL_STORAGE_KEYS.HAS_DOWNLOADED_FILE, 'true');

    dispatch(downloadDocument(res?.id, () => handleDownloadError(isManual)));
    setIsDownloading(false);

    dispatch(
      sendAnalyticEvent({
        event: 'save_as_modal_view'
      })
    );
  };

  const handleDownloadPrintDocument = (res: any) => {
    openPrintModalFromURL(res);
    setIsDownloading(false);
  };

  const downloadResult = (isManual?: boolean) => {
    const errorCallback = () => handleDownloadError(isManual);

    const successCallback = (res: ApiFile, inputFile?: { size: string }) => {
      handleDownloadSuccess(res, isManual, inputFile);
    };

    const sendSuccessAnalytics = (res: ApiFile) =>
      dispatch(
        sendAnalyticEvent({
          event: 'file_download_status',
          data: generateDownloadAnalyticsData({
            success: true,
            isManual: typeof isManual === 'boolean' ? isManual : true,
            place: PAGE_LINKS.PAYMENT_SUCCESS,
            fileData: res,
            fileId: res?.id
          })
        })
      );
    const sendFailedAnalytics = () =>
      dispatch(
        sendAnalyticEvent({
          event: 'file_download_status',
          data: generateDownloadAnalyticsData({
            success: false,
            isManual: typeof isManual === 'boolean' ? isManual : true,
            place: PAGE_LINKS.PAYMENT_SUCCESS,
            fileId: documentById?.id
          })
        })
      );

    if (sseDownloadId) {
      dispatch(downloadDocument(sseDownloadId, errorCallback, sendSuccessAnalytics));
      setIsDownloading(false);
      return;
    }

    if (dataToConvert?.serviceType === EServiceType.COMPRESSOR && willDownloadBySSE) return;

    const storedFileId = localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID);

    if (storedFileId) {
      console.log('storedFileId', storedFileId);
      dispatch(
        downloadDocument(storedFileId, sendFailedAnalytics, (res: any) => {
          sendSuccessAnalytics(res);
          setIsDownloading(false);
        })
      );
      return;
    }

    if (queryDocumentID) {
      console.log('queryDocumentID', queryDocumentID);
      dispatch(downloadDocument(queryDocumentID, sendFailedAnalytics, sendSuccessAnalytics));
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.PRINT_DOCUMENT)) {
      console.log('printDocument', documentById?.id);
      dispatch(downloadDocument(documentById?.id || '', handleDownloadPrintDocument, handleDownloadPrintDocument));

      return;
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID_TO_SEND)) {
      console.log('fileIdToSend', localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID_TO_SEND));
      dispatch(
        downloadDocument(
          localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID_TO_SEND) || '',
          sendFailedAnalytics,
          (res: any) => {
            sendSuccessAnalytics(res);
            setIsDownloading(false);
          }
        )
      );
      return;
    }

    // ab import export editor 244 test
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID)) {
      console.log('fileId', localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID));
      dispatch(
        downloadDocument(localStorage.getItem(LOCAL_STORAGE_KEYS.FILE_ID) || '', sendFailedAnalytics, (res: any) => {
          sendSuccessAnalytics(res);
          setIsDownloading(false);
        })
      );

      return;
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.DOWNLOAD_FROM_DASHBOARD)) {
      console.log('downloadFromDashboard', documentById.id);
      dispatch(
        downloadDocument(documentById.id, sendFailedAnalytics, (res: any) => {
          sendSuccessAnalytics(res);
          setIsDownloading(false);
        })
      );
    } else {
      switch (dataToConvert?.serviceType) {
        case EServiceType.EDITOR: {
          if (!editDocument?.to && (editDocument?.id || editDocument?.documentId)) {
            console.log('editor', editDocument?.id || editDocument?.documentId);
            dispatch(
              downloadDocument(editDocument?.id || editDocument?.documentId, sendFailedAnalytics, (res: any) => {
                sendSuccessAnalytics(res);
                setIsDownloading(false);
              })
            );
            setIsDownloading(false);
          }

          return;
        }

        default: {
          if (!documentById) {
            dispatch(
              getDocuments((res: any) => {
                DocumentsStorage.setDataToEvent(res?.files[0]);

                dispatch(downloadDocumentAndUpdatedDocumentId(res?.files[0]?.id, errorCallback, sendSuccessAnalytics));
                setIsDownloading(false);
              })
            );
            return;
          }

          if (dataToConvert?.serviceType === EServiceType.EDITOR) {
            console.log('editor', dataToConvert?.serviceType);
            const dataToEvent = DocumentsStorage.getDataToEvent();
            const fileId = dataToEvent && 'data' in dataToEvent ? dataToEvent?.data?.id : dataToEvent?.id;

            dispatch(
              downloadDocument(
                fileId,
                () => handleDownloadError(isManual),
                () => {
                  dispatch(
                    sendAnalyticEvent({
                      event: 'file_download_status',
                      data: generateDownloadAnalyticsData({
                        success: true,
                        isManual: true,
                        fileData: dataToEvent as ApiFile,
                        inputFile: { size: dataToConvert?.file?.size }
                      })
                    })
                  );
                }
              )
            );
          }

          if (
            (dataToConvert?.file?.to === InternalFileType.PDF && dataToConvert?.file?.from === InternalFileType.PDF) ||
            (dataToConvert?.to === InternalFileType.PDF && dataToConvert?.from === InternalFileType.PDF)
          ) {
            console.log('pdf', dataToConvert?.serviceType);
            if (dataToConvert?.serviceType !== EServiceType.SPLITTER) {
              dispatch(
                downloadDocument(
                  documentById?.id,
                  () => handleDownloadError(isManual),
                  () => {
                    dispatch(
                      sendAnalyticEvent({
                        event: 'file_download_status',
                        data: generateDownloadAnalyticsData({
                          success: true,
                          place: 'thank-you-page',
                          isManual: false,
                          fileData: documentById,
                          inputFile: { size: dataToConvert?.file?.size }
                        })
                      })
                    );
                  }
                )
              );
            }
            setIsDownloading(false);
            return;
          }

          if (localStorage.getItem(LOCAL_STORAGE_KEYS.PRINT_DOCUMENT)) {
            console.log('printDocument', documentById?.id);
            dispatch(
              downloadDocument(
                documentById?.id || '',
                (res: any) => {
                  openPrintModalFromURL(res);
                  successCallback(res);
                  setIsDownloading(false);
                },
                (res: any) => {
                  openPrintModalFromURL(res);
                  successCallback(res);
                  setIsDownloading(false);
                }
              )
            );

            localStorage.removeItem(LOCAL_STORAGE_KEYS.PRINT_DOCUMENT);
            return;
          }
        }
      }
    }
  };

  const handleDownloadFile = () => {
    dispatch(
      sendAnalyticEvent({
        event: 'download_file_tap',
        data: {
          type: documentById?.format
        }
      })
    );

    if (isIosDevice) {
      console.log('ios device');
    } else {
      if (dataToConvert?.serviceType === EServiceType.EDITOR && editDocument?.to === InternalFileType.PDF) {
        dispatch(
          downloadDocument(
            editDocument?.documentId || editDocument?.id,
            () => handleDownloadError(),
            () => {
              dispatch(
                sendAnalyticEvent({
                  event: 'file_download_status',
                  data: generateDownloadAnalyticsData({
                    success: true,
                    isManual: true,
                    fileData: editDocument,
                    inputFile: { size: dataToConvert?.file?.size }
                  })
                })
              );
            }
          )
        );
      } else {
        const dataToEvent = DocumentsStorage.getDataToEvent();
        const dataToEventFileId = dataToEvent && 'data' in dataToEvent ? dataToEvent?.data?.id : dataToEvent?.id;
        // додати interface для sse
        dispatch(
          downloadDocument(
            dataToEventFileId || documentById?.id,
            () => handleDownloadError(),
            () => {
              dispatch(
                sendAnalyticEvent({
                  event: 'file_download_status',
                  data: generateDownloadAnalyticsData({
                    success: true,
                    isManual: true,
                    fileData: dataToEvent as ApiFile,
                    inputFile: { size: dataToConvert?.file?.size }
                  })
                })
              );
            }
          )
        );
      }
    }
  };

  const renderThankYouBlockComponent = () => {
    return (
      <ThankYouBlock
        isDownloading={isDownloading && !message}
        handleDownloadFile={handleDownloadFile}
        handleClickDashboard={handleClickDashboard}
      />
    );
  };

  useEffect(() => {
    const sendFileViaEmailPayload = localStorage.getItem(LOCAL_STORAGE_KEYS.SEND_FILE_VIA_EMAIL_PAYLOAD);
    if (sendFileViaEmailPayload) {
      dispatch(sendFileViaEmail(JSON.parse(sendFileViaEmailPayload)))
        .then(() => {
          showToast('success', 'Email sent successfully', 5000, 'sendTo');
          dispatch(sendAnalyticEvent({ event: 'send_file_success' }));
        })
        .catch((error: Error) => {
          showToast('error', 'Failed to send email', 5000, 'sendTo');
          console.error('Error sending email:', error);
        });
    }
  }, [dispatch]);

  return {
    downloadResult,
    renderThankYouBlockComponent
  };
};
